import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chakra-ui/react';

import {
  apiCombineQuotes,
  apiUncombineQuote,
  type QuoteGroup,
} from 'api/quote-marketplace';

interface UseCombineQuotesProps {
  contactRef: string;
  onCombineSuccess?: (group: QuoteGroup) => void;
  onUncombineSuccess?: () => void;
}

export interface UseCombineQuotesReturn {
  combine: (quoteIds: number[]) => void;
  uncombine: (quoteId: number) => void;
  isCombining: boolean;
  isUncombining: boolean;
}

export const useCombineQuotes = ({
  contactRef,
  onCombineSuccess,
  onUncombineSuccess,
}: UseCombineQuotesProps): UseCombineQuotesReturn => {
  const queryClient = useQueryClient();
  const toast = useToast();

  const refetchQuotes = () => {
    queryClient.refetchQueries({
      queryKey: ['contact-rfqs-poll', contactRef],
    });
  };

  const combineMutation = useMutation({
    mutationFn: (quoteIds: number[]) => apiCombineQuotes(quoteIds),
    onSuccess: (group) => {
      refetchQuotes();
      toast({
        title: 'Quotes combined',
        description: 'The quotes have been combined into a group.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      onCombineSuccess?.(group);
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to combine quotes',
        description:
          error.message || 'An error occurred while combining quotes.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  const uncombineMutation = useMutation({
    mutationFn: (quoteId: number) => apiUncombineQuote(quoteId),
    onSuccess: () => {
      refetchQuotes();
      toast({
        title: 'Quote uncombined',
        description: 'The quote has been removed from the group.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      onUncombineSuccess?.();
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to uncombine quote',
        description:
          error.message || 'An error occurred while uncombining the quote.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    },
  });

  return {
    combine: (quoteIds: number[]) => combineMutation.mutate(quoteIds),
    uncombine: (quoteId: number) => uncombineMutation.mutate(quoteId),
    isCombining: combineMutation.isPending,
    isUncombining: uncombineMutation.isPending,
  };
};
